<template>
    <div class="atp" :style="cssProps">
        <!-- Select-style trigger -->
        <button
            type="button"
            :class="['atp__trigger', { 'atp__trigger--open': isOpen, 'atp__trigger--disabled': !timeItems.length }]"
            @click="toggle"
        >
            <span :class="['atp__value', { 'atp__value--placeholder': !displayValue }]">
                {{ displayValue || placeholderText }}
            </span>
            <svg class="atp__chevron" :class="{ 'atp__chevron--open': isOpen }" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
            </svg>
        </button>

        <!-- Dropdown with two independent wheels -->
        <transition name="atp-fade">
            <div v-if="isOpen && timeItems.length" class="atp__dropdown">
                <div class="atp__wheels">
                    <time-wheel-column
                        class="atp__col"
                        :items="hours"
                        :value="selectedHour"
                        @input="onHourChange"
                    />
                    <div class="atp__colon">:</div>
                    <time-wheel-column
                        class="atp__col"
                        :items="currentMinutes"
                        :value="selectedMinute"
                        @input="onMinuteChange"
                    />
                    <div class="atp__highlight"></div>
                </div>
                <div class="atp__actions">
                    <button type="button" class="atp__done" @click="close">
                        {{ doneText }}
                    </button>
                </div>
            </div>
        </transition>

        <div v-if="isOpen && !timeItems.length" class="atp__dropdown atp__dropdown--empty">
            {{ emptyText }}
        </div>
    </div>
</template>

<script>
import moment from 'moment-timezone';
import TimeWheelColumn from './TimeWheelColumn.vue';

export default {
    components: { TimeWheelColumn },

    props: ['itemProperty', 'properties'],

    data() {
        return {
            isOpen: false,
            selectedHour: '',
            selectedMinute: '',
        };
    },

    computed: {
        s_item_property: {
            get() {
                return this.itemProperty
            },

            set(val) {
                this.$emit('update:itemProperty', val)
            },
        },

        dateFormat() {
            return process.env.VUE_APP_SERVER.toLowerCase() == 'ru' ? 'DD-MM-YYYY' : 'YYYY-MM-DD';
        },

        // Same data the old <select> used, but cached & always an array.
        timeItems() {
            const range = this.getTimeRange();
            return Array.isArray(range) ? range : [];
        },

        // Unique hours in order of appearance, e.g. ['9', '10', '11'].
        hours() {
            const seen = {};
            const list = [];
            this.timeItems.forEach(item => {
                const h = item.split(':')[0];
                if (!seen[h]) { seen[h] = true; list.push(h); }
            });
            return list;
        },

        // Map hour -> ordered list of valid minutes for that hour.
        minutesByHour() {
            const map = {};
            this.timeItems.forEach(item => {
                const parts = item.split(':');
                const h = parts[0];
                const m = parts[1];
                if (!map[h]) map[h] = [];
                if (map[h].indexOf(m) === -1) map[h].push(m);
            });
            return map;
        },

        currentMinutes() {
            return this.minutesByHour[this.selectedHour] || [];
        },

        // Stored value, kept in the original 'H:mm' format.
        selectedValue() {
            if (this.selectedHour === '' || this.selectedMinute === '') return '';
            return this.selectedHour + ':' + this.selectedMinute;
        },

        // Padded for nicer display only (stored value stays 'H:mm').
        displayValue() {
            if (!this.selectedValue) return '';
            const h = this.selectedHour.length < 2 ? '0' + this.selectedHour : this.selectedHour;
            return h + ':' + this.selectedMinute;
        },

        placeholderText() {
            return this.langText('select_time', '--:--');
        },

        doneText() {
            return this.langText('done', 'OK');
        },

        emptyText() {
            return this.langText('no_available_time', '—');
        },

        cssProps() {
            let accent = '#111827';
            try {
                accent = this.$root.hotelColor() || accent;
            } catch (e) { /* keep default */ }
            return { '--atp-accent': accent };
        },
    },

    watch: {
        // Re-resolve when the available times change (e.g. the date changed).
        timeItems() {
            this.$nextTick(() => this.syncFromValue());
        },

        'itemProperty.value'(newVal) {
            if (newVal === this.selectedValue) return;
            this.$nextTick(() => this.syncFromValue());
        },
    },

    mounted() {
        this.syncFromValue();
        document.addEventListener('click', this.onDocumentClick, true);
    },

    beforeDestroy() {
        document.removeEventListener('click', this.onDocumentClick, true);
    },

    methods: {
        // ----- dropdown / wheel behaviour --------------------------------

        toggle() {
            if (!this.timeItems.length) { this.isOpen = true; return; }
            this.isOpen = !this.isOpen;
            if (this.isOpen) this.syncFromValue();
        },

        close() {
            this.isOpen = false;
        },

        onDocumentClick(event) {
            if (!this.isOpen) return;
            if (this.$el && !this.$el.contains(event.target)) {
                this.isOpen = false;
            }
        },

        // Parse the stored value into the two wheels; default to the first
        // valid slot when nothing (valid) is selected yet.
        syncFromValue() {
            if (!this.timeItems.length) {
                this.selectedHour = '';
                this.selectedMinute = '';
                return;
            }

            let value = this.s_item_property.value;
            if (!value || this.timeItems.indexOf(value) === -1) {
                value = this.closestToNowValue();
            }

            const parts = value.split(':');
            this.selectedHour = parts[0];
            this.selectedMinute = parts[1];
            this.commitValue();
        },

        onHourChange(hour) {
            this.selectedHour = hour;
            // Keep the minute valid for the newly selected hour.
            const minutes = this.minutesByHour[hour] || [];
            if (minutes.indexOf(this.selectedMinute) === -1) {
                this.selectedMinute = minutes.length ? minutes[0] : '';
            }
            this.commitValue();
        },

        onMinuteChange(minute) {
            this.selectedMinute = minute;
            this.commitValue();
        },

        commitValue() {
            const value = this.selectedValue;
            if (!value || this.timeItems.indexOf(value) === -1) return;
            if (this.s_item_property.value === value) return;
            // Mutate in place exactly like the old <select v-model> did, so the
            // parent (bound with :item-property.sync) keeps the same object ref.
            this.s_item_property.value = value;
        },

        // Pick the available slot whose clock time is nearest to "now".
        closestToNowValue() {
            if (!this.timeItems.length) return '';
            const n = moment();
            const nowMins = n.hours() * 60 + n.minutes();
            let best = this.timeItems[0];
            let bestDiff = Infinity;
            this.timeItems.forEach(item => {
                const sep = item.indexOf(':');
                const diff = Math.abs(
                    parseInt(item, 10) * 60 +
                    parseInt(item.slice(sep + 1), 10) -
                    nowMins
                );
                if (diff < bestDiff) { bestDiff = diff; best = item; }
            });
            return best;
        },

        langText(key, fallback) {
            try {
                const text = this.$root.getLangText(this.$root.keyWords[key]);
                return text || fallback;
            } catch (e) {
                return fallback;
            }
        },

        // ----- original time-range logic (unchanged) ---------------------

        getTimeRange() {
            let start = moment('2020-04-20 00:00', 'YYYY-MM-DD H:mm');
            let end = moment('2020-04-20 23:59', 'YYYY-MM-DD H:mm');
            let nowTime = moment();
            let calendarDate = moment();

            var dateProp = this.properties.filter(item => item.ptype_id == 'date');
            if (dateProp.length > 0 && dateProp[0].value) {
                calendarDate = moment(dateProp[0].value, this.dateFormat);
            }

            if ( this.s_item_property.schedule_id !== null) {
                var activeScheduleDays = [];
                this.s_item_property.schedule_kit.days.map(d_item => {
                    if (d_item.days.filter((day, index) => day && calendarDate.day() == index).length > 0)
                        activeScheduleDays.push(d_item);
                });

                if (activeScheduleDays.length > 0) {
                    start = moment(activeScheduleDays[0].start_time, 'HH:mm:ss');
                    end = moment(activeScheduleDays[0].end_time, 'HH:mm:ss');
                }
            }

            try {
                // do same time for diff
                end.set("year", 2020);
                end.set("month", 1);
                end.set("date", 20);

                start.set("year", 2020);
                start.set("month", 1);
                start.set("date", 20);

                nowTime.set("year", 2020);
                nowTime.set("month", 1);
                nowTime.set("date", 20);


                if (dateProp.length > 0 && dateProp[0].value == moment().format(this.dateFormat) && start.diff(nowTime) < 0) {
                    const now = moment();
                    start = now;
                    end.set({'year': now.get('year'), 'month': now.get('month'), 'date': now.get('date')});

                    var interval = this.timeInterval(this.s_item_property);

                    var roundTime = this.roundTime(start.format('HH:mm'), interval);
                    /*start = moment(new Date());*/
                    start.set({hour: roundTime.hour, minute: roundTime.min, second: 0, millisecond: 0});
                    start.add(interval, 'minutes');

                    if (start.diff(end) > 0)
                        return false;
                }

                return this.loopStartAndEndTimes(this.s_item_property, start, end);
            } catch (e) {
                return this.loopStartAndEndTimes(this.s_item_property, start, end);
            }
        },

        loopStartAndEndTimes(item, start, end) {
            if (start > end) {
                end = end.add(1, 'days');
            }

            let items = [];
            var interval = this.timeInterval(item);
            for (let m = moment(start); m.isBefore(end); m.add(interval, 'minutes')) {
                items.push(m.format('H:mm'));
            }
            items.push(end.format('H:mm'));

            return items;
        },

        // A step of 0 (or anything non-numeric) would never advance the slot loop and hang the page.
        timeInterval(item) {
            var interval = parseInt(item.extra && item.extra.time_interval);
            return interval > 0 ? interval : 30;
        },

        roundTime(time, minutesToRound) {
            let [hours, minutes] = time.split(':');
            hours = parseInt(hours);
            minutes = parseInt(minutes);

            // Convert hours and minutes to time in minutes
            time = (hours * 60) + minutes;

            let rounded = Math.round(time / minutesToRound) * minutesToRound;
            let rHr = ''+Math.floor(rounded / 60)
            let rMin = ''+ rounded % 60

            return {'hour': rHr, 'min':rMin};
        },

        checkScheduleInTime(currentDate, item, timezone) {
            var start_time = item.start_time;
            var end_time = item.end_time;

            var currentCopyDate = new Date(moment.tz(new Date(), timezone).format('YYYY/MM/DD HH:mm:ss'));
            currentCopyDate.setHours(start_time.split(':')[0]);
            currentCopyDate.setMinutes(start_time.split(':')[1]);
            currentCopyDate.setSeconds(0);
            start_time = currentCopyDate;

            var h_end = end_time.split(':')[0];
            var m_end = end_time.split(':')[1];

            currentCopyDate = new Date(moment.tz(new Date(), timezone).format('YYYY/MM/DD HH:mm:ss'));
            currentCopyDate.setHours(h_end);
            currentCopyDate.setMinutes(m_end);
            currentCopyDate.setSeconds(0);
            end_time = currentCopyDate;

            if (start_time >= end_time) {
                var start_time_mo = moment(start_time)
                    .subtract(h_end, 'hours')
                    .subtract(m_end, 'minutes')
                    .subtract(1, 'seconds');

                var end_time_mo = moment(end_time)
                    .subtract(h_end, 'hours')
                    .subtract(m_end, 'minutes')
                    .subtract(1, 'seconds');

                var current_time_mo = moment()
                    .subtract(h_end, 'hours')
                    .subtract(m_end, 'minutes')
                    .subtract(1, 'seconds');

                var new_start_time = this.setHoursAndMinutes(start_time_mo.hours(), start_time_mo.minutes(), start_time_mo.seconds(), timezone);
                var new_end_time = this.setHoursAndMinutes(end_time_mo.hours(), end_time_mo.minutes(), end_time_mo.seconds(), timezone);
                var new_current_time = this.setHoursAndMinutes(current_time_mo.hours(), current_time_mo.minutes(), current_time_mo.seconds(), timezone);

                return new_current_time.isBetween(new_start_time, new_end_time)
            }

            return currentDate >= start_time && currentDate <= end_time;
        },

        setHoursAndMinutes(h, m, s, timezone) {
            var currentCopyDate = new Date(moment.tz(new Date(), timezone).format('YYYY/MM/DD HH:mm:ss'));
            currentCopyDate.setHours(h);
            currentCopyDate.setMinutes(m);
            currentCopyDate.setSeconds(s);
            return moment(currentCopyDate);
        },
    }
}
</script>

<style scoped>
.atp {
    position: relative;
    width: 100%;
}

/* ----- trigger (looks like the old select) ----- */
.atp__trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 0.55rem 0.75rem;
    font-size: 14px;
    line-height: 1.25;
    cursor: pointer;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.atp__trigger:hover {
    border-color: #d1d5db;
}

.atp__trigger--open {
    border-color: var(--atp-accent, #111827);
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.04);
}

.atp__trigger--disabled {
    opacity: 0.6;
}

.atp__value {
    color: #111827;
    font-variant-numeric: tabular-nums;
}

.atp__value--placeholder {
    color: #9ca3af;
}

.atp__chevron {
    width: 1rem;
    height: 1rem;
    fill: #6b7280;
    transition: transform 0.2s ease;
    flex-shrink: 0;
}

.atp__chevron--open {
    transform: rotate(180deg);
}

/* ----- dropdown ----- */
.atp__dropdown {
    position: absolute;
    z-index: 50;
    left: 0;
    width: fit-content;
    margin-top: 6px;
    background: #ffffff;
    border: 1px solid #eef0f3;
    border-radius: 16px;
    box-shadow: 0 14px 36px rgba(0, 0, 0, 0.12);
    padding: 8px 10px 0;
}

.atp__dropdown--empty {
    padding: 16px;
    text-align: center;
    color: #9ca3af;
    font-size: 14px;
}

.atp__wheels {
    position: relative;
    display: flex;
    align-items: stretch;
    justify-content: center;
    padding: 6px 4px;
}

.atp__col {
    --twc-accent: var(--atp-accent, #111827);
    position: relative;
    z-index: 2;
    width: 100px;
    flex: 0 0 100px;
}

.atp__colon {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: 500;
    color: #9ca3af;
    padding: 0 4px;
    z-index: 3;
}

/* center selection band spanning both wheels */
.atp__highlight {
    position: absolute;
    left: 6px;
    right: 6px;
    top: 50%;
    height: 36px;
    margin-top: -18px;
    border-top: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
    border-radius: 10px;
    background: rgba(0, 0, 0, 0.03);
    pointer-events: none;
    z-index: 1;
}

.atp__actions {
    display: flex;
    justify-content: flex-end;
    border-top: 1px solid #f3f4f6;
    padding: 8px 4px;
    margin-top: 4px;
}

.atp__done {
    background: var(--atp-accent, #111827);
    color: #ffffff;
    border: none;
    border-radius: 8px;
    padding: 6px 18px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    outline: none;
}

/* dropdown transition (keep the translateX(-50%) centering intact) */
.atp-fade-enter-active,
.atp-fade-leave-active {
    transition: opacity 0.16s ease;
}

.atp-fade-enter,
.atp-fade-leave-to {
    opacity: 0;
}
</style>
